import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { ICompletionFlow, ICompletionGroupType, ITerminal } from '../terminalAdministration.service';
import { objectValidator, ObjectValidators, ValidatorMap } from './objectValidator';

const completionFlows: ICompletionFlow[] = Object.values(ICompletionFlow);
const completionGroupTypes: ICompletionGroupType[] = Object.values(ICompletionGroupType);

/** Mirrors the `sqlQuery` control in `create-terminal.component.ts` and the `maxlength` on its textarea. Exported so the import cannot become a back door for payloads the dialog rejects. */
export const TERMINAL_SQL_QUERY_MAX_LENGTH = 200000;

/**
 * Validation for terminals, used by both the create/edit dialogs' contract and by `TerminalTransferService`'s import pipeline.
 *
 * **Where mandatory-ness lives.** `TerminalSchema` marks every column but `alias` as `required: false`, so a blank cell reaches this service instead of being rejected by the parser with a generic `missing-properties` row error. That is deliberate: it routes every missing value through `objectValidator`, so the user
 * gets the same per-field translated message the dialog gives.
 *
 * **Why the map is keyed on `ITerminal` and not on the file's column names.** The rules mirror the dialog's form controls (`create-terminal.component.ts:81`-`137`), and those controls are named after the entity — `terminalTemplateId`, `workTaskId`, `nextId`. The file's `terminalTemplateAlias` / `workTaskAlias` /
 * `nextTerminalAlias` columns are resolved to those ids by the transfer service before validation runs, so validating the resolved entity is what makes the comparison against the dialog a checkable statement.
 *
 * The mirror is deliberately imperfect in one place: `stationFilter` is required in the dialog but nullable in the backend, so the import accepts a blank one. Its format rules still apply.
 */
@Injectable({ providedIn: 'root' })
export class TerminalValidationService {
	constructor(private translator: TranslateService) {}

	validators(validateId: boolean = true): ValidatorMap<ITerminal> {
		const validator: ValidatorMap<ITerminal> = {
			id: [ObjectValidators.required],
			alias: [ObjectValidators.required, ObjectValidators.maxLength(255), ObjectValidators.pattern(ValidatorPatterns.Alias, 'mes.administration.terminal.dialog.alias.error.pattern')],
			name: [ObjectValidators.required, ObjectValidators.maxLength(255), ObjectValidators.pattern(ValidatorPatterns.Description, 'mes.administration.terminal.dialog.name.error.pattern')],
			// The dialog picks this from a fixed list, so import is the only way a bad value can arrive.
			completionFlow: [ObjectValidators.required, ObjectValidators.oneOf(completionFlows)],
			// No `required`: the transfer service defaults a blank cell to Groups, as the API does.
			completionGroupType: [ObjectValidators.oneOf(completionGroupTypes)],
			stationFilter: [
				// ObjectValidators.required, This is required in frontend but is nullable in the backend, so we cannot validate it as required in the import.
				ObjectValidators.maxLength(2),
				ObjectValidators.pattern(ValidatorPatterns.WholeNumber, 'mes.administration.terminal.dialog.stationFilter.error.pattern'),
			],
			terminalTemplateId: [ObjectValidators.required, ObjectValidators.maxLength(36), ObjectValidators.pattern(ValidatorPatterns.GuidPattern)],
			// No `required` — the dialog does not require a work task either.
			workTaskId: [ObjectValidators.maxLength(36), ObjectValidators.pattern(ValidatorPatterns.GuidPattern)],
			nextId: [ObjectValidators.maxLength(36), ObjectValidators.pattern(ValidatorPatterns.GuidPattern)],
			sqlQuery: [ObjectValidators.maxLength(TERMINAL_SQL_QUERY_MAX_LENGTH)],
			// Unvalidated in the dialog: free-text lists chosen from existing values.
			workTaskCategories: [],
			incidentCodeGroups: [],
			// Not importable and edited through its own component; carried through untouched.
			printConfigurations: [],
		};

		if (!validateId) {
			delete validator.id;
		}

		return validator;
	}

	validateTerminal(terminal: ITerminal, validateId: boolean = true): Record<string, ValidationErrors> {
		return objectValidator.validateObject(terminal, this.validators(validateId), this.translator);
	}
}
